import React, { useState, useEffect } from "react";
import { fetchScores } from "../services/api";
import { Loader2, Radio, Calendar } from "lucide-react";

const LEAGUES = [
  { id: "epl", name: "EPL", color: "#3D195B" },
  { id: "aleague", name: "A-League", color: "#E4002B" },
  { id: "bbl", name: "BBL", color: "#00B140" },
  { id: "wbbl", name: "WBBL", color: "#E91E8C" },
];

const statusLabel = (status) => {
  switch (status) {
    case "live": return "LIVE";
    case "ft": return "FT";
    case "ht": return "HT";
    default: return "";
  }
};

const formatTime = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  const day = d.toLocaleDateString("en-AU", { weekday: "short", timeZone: "Australia/Sydney" });
  const time = d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Australia/Sydney" });
  return `${day} ${time}`;
};

const CRICKET_LEAGUES = ["bbl", "wbbl"];

const MatchCard = ({ match, index, isCricket }) => {
  const isLive = match.status === "live";
  const isHT = match.status === "ht";
  const isFinished = match.status === "ft";
  const isUpcoming = match.status === "upcoming";
  const isActive = isLive || isHT;

  return (
    <div
      data-testid={`match-card-${index}`}
      className={`p-3 rounded-xl border transition-all ${
        isActive
          ? "border-red-200 bg-red-50/50"
          : "border-gray-100 bg-white hover:border-gray-200"
      }`}
    >
      {/* Status badge */}
      <div className="flex items-center justify-between mb-2.5">
        {isLive ? (
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-600">
            <Radio size={10} className="animate-pulse" />
            {match.match_time || "LIVE"}
          </span>
        ) : isHT ? (
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-600">
            <Radio size={10} />
            HT
          </span>
        ) : isFinished ? (
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">FT</span>
        ) : (
          <span className="text-[10px] font-medium text-gray-400">{formatTime(match.start_time)}</span>
        )}
        {match.venue && (
          <span className="text-[9px] text-gray-300 truncate max-w-[80px]">{match.venue}</span>
        )}
      </div>

      {/* Teams and scores */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold truncate pr-2 ${isActive ? "text-gray-900" : "text-gray-700"}`}>
            {match.home_team}
          </span>
          <span className={`${isCricket ? "text-xs" : "text-sm"} font-black tabular-nums min-w-[20px] text-right ${
            isUpcoming ? "text-gray-300" : isActive ? "text-gray-900" : "text-gray-600"
          }`}>
            {isUpcoming && !match.home_score ? "-" : (match.home_score === "" || match.home_score === 0 || match.home_score === "0") && isUpcoming ? "-" : match.home_score || (isUpcoming ? "-" : "0")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-xs font-semibold truncate pr-2 ${isActive ? "text-gray-900" : "text-gray-700"}`}>
            {match.away_team}
          </span>
          <span className={`${isCricket ? "text-xs" : "text-sm"} font-black tabular-nums min-w-[20px] text-right ${
            isUpcoming ? "text-gray-300" : isActive ? "text-gray-900" : "text-gray-600"
          }`}>
            {isUpcoming && !match.away_score ? "-" : (match.away_score === "" || match.away_score === 0 || match.away_score === "0") && isUpcoming ? "-" : match.away_score || (isUpcoming ? "-" : "0")}
          </span>
        </div>
      </div>

      {/* Cricket result summary for completed or live matches */}
      {isCricket && match.match_time && match.match_time !== "FT" && (
        <p className={`text-[10px] mt-2 pt-1.5 border-t border-gray-100 truncate ${
          isFinished ? "text-gray-500 italic" : "text-red-600 font-medium"
        }`}>
          {match.match_time}
        </p>
      )}
    </div>
  );
};

const formatSeasonStart = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00+10:00");
  return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", timeZone: "Australia/Sydney" });
};

const LiveScores = () => {
  const [activeLeague, setActiveLeague] = useState("epl");
  const [matches, setMatches] = useState([]);
  const [seasonStart, setSeasonStart] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchScores(activeLeague);
        if (!cancelled) {
          if (data && data.matches !== undefined) {
            setMatches(data.matches);
            setSeasonStart(data.season_start || null);
          } else {
            setMatches(Array.isArray(data) ? data : []);
            setSeasonStart(null);
          }
        }
      } catch (err) {
        console.error("Failed to load scores:", err);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    const interval = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeLeague]);

  // Already sorted by backend: live first, then upcoming, then FT
  const sorted = matches;

  return (
    <div data-testid="live-scores-widget" className="lg:col-span-1">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1 h-6 bg-red-600 rounded-full" />
        <h2 className="text-lg font-bold text-gray-900">Live Scores</h2>
        {Array.isArray(matches) && matches.some((m) => m.status === "live") && (
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        )}
      </div>

      {/* League tabs */}
      <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-1">
        {LEAGUES.map((league) => (
          <button
            key={league.id}
            data-testid={`league-tab-${league.id}`}
            onClick={() => setActiveLeague(league.id)}
            className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${
              activeLeague === league.id
                ? "text-white shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            style={activeLeague === league.id ? { backgroundColor: league.color } : {}}
          >
            {league.name}
          </button>
        ))}
      </div>

      {/* Matches */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" size={20} />
        </div>
      ) : sorted.length === 0 ? (
        seasonStart && new Date(seasonStart) > new Date() ? (
          <div className="flex flex-col items-center justify-center py-6 gap-2">
            <Calendar size={20} className="text-gray-300" />
            <p className="text-xs font-semibold text-gray-500">Season starts</p>
            <p className="text-sm font-bold text-gray-700">{formatSeasonStart(seasonStart)}</p>
          </div>
        ) : (
          <p className="text-xs text-gray-400 text-center py-6">
            {CRICKET_LEAGUES.includes(activeLeague) ? "No upcoming matches" : "No matches this week"}
          </p>
        )
      ) : (
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
          {sorted.map((match, i) => (
            <MatchCard key={`${match.home_team}-${match.away_team}`} match={match} index={i} isCricket={CRICKET_LEAGUES.includes(activeLeague)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveScores;
